import { computed, Injectable, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import { StorageVariables } from '@app/shared/enums/storage.enums';
import { Device } from '@capacitor/device';
import { Language } from '@app/core/types/common/common.types';
import { AppConfigService } from '@app/core/services/config.service';
import { CultureInfo } from '@app/core/types/config/config.types';
import { StorageService } from '@app/core/services/storage.service';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { cultureToAlpha2 } from '@app/shared/utils/language.util';

@Injectable({
  providedIn: 'root',
})
export class TranslationSandbox {
  private readonly translocoService = inject(TranslocoService);
  private readonly appConfigService = inject(AppConfigService);
  private readonly storageService = inject(StorageService);

  initFinished$ = new BehaviorSubject(false);
  selectedCulture$ = new BehaviorSubject<CultureInfo>(null);
  languageLoading$ = new BehaviorSubject<boolean>(false);
  langChanges$ = this.translocoService.langChanges$;
  // application language that represents the language saved in API
  applicationLanguage = signal<CultureInfo>(null);
  private readonly activeLang = toSignal(this.translocoService.langChanges$, {
    initialValue: this.translocoService.getActiveLang(),
  });
  currentLocale = computed(() => cultureToAlpha2(this.activeLang()));

  private cultureInfos: CultureInfo[] = [];
  private defaultCultureCode: string;
  private defaultAlpha3Code: string;

  // TODO REPLACE I18N COMPONENTS
  async init(): Promise<void> {
    this.cultureInfos = this.appConfigService.cultureInfos;
    this.defaultCultureCode = this.appConfigService.defaultCultureCode;
    this.defaultAlpha3Code = this.appConfigService.defaultAlpha3Code;

    this.translocoService.setAvailableLangs(this.cultureInfos.map((e) => e.cultureCode));
    this.translocoService.setDefaultLang(this.defaultCultureCode);

    const savedLanguage = await this.storageService.getValue(StorageVariables.Language);
    const lang = savedLanguage ?? this.defaultCultureCode;
    this.translocoService.setActiveLang(lang);
    await firstValueFrom(this.translocoService.selectTranslation(lang));

    const startupLanguage = await this.getStartupLanguage();
    this.initFinished$.next(true);
    this.selectedCulture$.next(this.cultureInfos.find((culture) => culture.cultureCode === startupLanguage));
  }

  /**
   * Gets the current culture
   */
  getCulture(): CultureInfo {
    const currentCulture = this.cultureInfos.find(
      (culture) => culture.cultureCode === this.translocoService.getActiveLang()
    );
    return { ...currentCulture, languageAlpha3Code: currentCulture?.languageAlpha3Code ?? this.defaultAlpha3Code };
  }

  getCultures(): CultureInfo[] {
    return this.cultureInfos;
  }

  /**
   * function to translate. use translationSandbox.translate('welcome')
   * @param key translation key
   * @param params translation params
   */
  translate(key: string, params: Record<string, unknown> = null): string {
    return this.translocoService.translate(key, params);
  }

  /**
   * Function to change current language
   * @param lang
   */
  changeLanguage(lang: string, saveToLocalStorage: boolean = true): void {
    if (lang === this.translocoService.getActiveLang()) {
      return;
    }

    try {
      this.languageLoading$.next(true);
      this.translocoService.setActiveLang(lang);

      if (saveToLocalStorage) {
        this.storageService.setValue(StorageVariables.Language, lang);
      }
      this.selectedCulture$.next(this.cultureInfos.find((culture) => culture.cultureCode === lang));
    } finally {
      this.languageLoading$.next(false);
    }
  }

  async getStartupLanguage(): Promise<string> {
    try {
      const savedLanguage = await this.storageService.getValue(StorageVariables.Language);
      if (savedLanguage) {
        // We prioritize culture codes when checking language string saved in local storage
        const cultureMatchingCultureCode = this.cultureInfos.find(
          (cultureInfo) => cultureInfo.cultureCode === savedLanguage
        );
        if (cultureMatchingCultureCode) {
          return cultureMatchingCultureCode.cultureCode;
        }
        // If no matches are found we search for culture with matching alpha3Code instead
        const cultureMatchingAlpha3Code = this.cultureInfos.find(
          (cultureInfo) => cultureInfo.languageAlpha3Code === savedLanguage
        );
        if (cultureMatchingAlpha3Code) {
          return cultureMatchingAlpha3Code.cultureCode;
        }
      }

      //getLanguageCode returns in this format en-US, we just need first 2 characters
      const deviceLanguage = (await Device.getLanguageCode())?.value?.split('-')?.[0];
      const foundLanguage = this.cultureInfos.find((language) => language.languageAlpha2Code === deviceLanguage);
      return foundLanguage?.cultureCode ?? this.defaultCultureCode;
    } catch {
      return this.defaultCultureCode;
    }
  }

  getApplicationLanguage(): string {
    return (this.appConfigService.config.languages as Language[]).find(
      (language: Language) => language.alpha3Code.toLowerCase() === this.getCulture().languageAlpha3Code.toLowerCase()
    )?.id;
  }

  setApplicationLanguage(appLanguage: CultureInfo): void {
    this.applicationLanguage.set(appLanguage);
  }
}
